import { assertIsBoolean } from './container/container.assertions';
import DropShadow from './drop-shadow';

/** Component is the base of all other components that extends from this class. */
export default class Component extends HTMLElement {
	public static observedAttributes = ['cursor', 'height', 'max-height', 'max-width', 'min-height', 'min-width', 'opacity', 'width'];

	/** The bottom property defines how many pixels the component will be positioned relative to the bottom edge of the parent container. */
	public set bottom(value: number) {
		if (this.#bottom === value) {
			return;
		}
		this.#bottom = value;
		this.#bottomChanged = true;
		this.invalidateProperties();
	}

	public get bottom(): number {
		return this.#bottom;
	}

	/** The cursor property sets the mouse cursor, if any, to show when the mouse pointer is over a Component. */
	public set cursor(value: 'default' | 'pointer') {
		assertIsValidCursor(value);
		this.#cursor = value;
		this.#cursorChanged = true;
		this.invalidateProperties();
	}

	public get cursor(): 'default' | 'pointer' {
		return this.#cursor;
	}

	public set effects(value: DropShadow[] | null) {
		assertIsValidEffects(value);
		this.#effects = value;
		this.#effectsChanged = true;
		this.invalidateProperties();
	}

	public get effects(): DropShadow[] | null {
		return this.#effects;
	}

	/**
	 * Height must be a number, "fill" or "hug".
	 *
	 * If a number, it represents the height in pixels, and must be a non negative number.
	 *
	 * If "fill", the component should expand it's height as much as possible.
	 *
	 * If "hug", the component should shrink it's height as much as possible.
	 */
	public set height(value: 'fill' | 'hug' | number) {
		assertIsValidSize(value);
		this.#height = value;
		this.#heightChanged = true;
		this.invalidateProperties();
	}

	public get height(): 'fill' | 'hug' | number {
		return this.#height;
	}

	/** The left property defines how many pixels the component will be positioned relative to the left edge of the parent container. */
	public set left(value: number) {
		if (this.#left === value) {
			return;
		}
		this.#left = value;
		this.#leftChanged = true;
		this.invalidateProperties();
	}

	public get left(): number {
		return this.#left;
	}

	/**
	 * The maxHeight property sets the maximum height of the Component in pixels.
	 *
	 * It prevents the used value of the height property from becoming larger than the value specified for maxHeight.
	 */
	public set maxHeight(value: number) {
		assertIsNonNegative(value);
		this.#maxHeight = value;
		this.#maxHeightChanged = true;
		this.invalidateProperties();
	}

	public get maxHeight(): number {
		return this.#maxHeight;
	}

	/**
	 * The maxWidth property sets the maximum width of the Component in pixels.
	 *
	 * It prevents the used value of the width property from becoming larger than the value specified for maxWidth.
	 */
	public set maxWidth(value: number) {
		assertIsNonNegative(value);
		this.#maxWidth = value;
		this.#maxWidthChanged = true;
		this.invalidateProperties();
	}

	public get maxWidth(): number {
		return this.#maxWidth;
	}

	/**
	 * The minHeight property sets the minimum height of the Component in pixels.
	 *
	 * It prevents the used value of the height property from becoming smaller than the value specified for minHeight.
	 */
	public set minHeight(value: number) {
		assertIsNonNegative(value);
		this.#minHeight = value;
		this.#minHeightChanged = true;
		this.invalidateProperties();
	}

	public get minHeight(): number {
		return this.#minHeight;
	}

	/**
	 * The minWidth property sets the minimum width of the Component in pixels.
	 *
	 * It prevents the used value of the width property from becoming smaller than the value specified for minWidth.
	 */
	public set minWidth(value: number) {
		assertIsNonNegative(value);
		this.#minWidth = value;
		this.#minWidthChanged = true;
		this.invalidateProperties();
	}

	public get minWidth(): number {
		return this.#minWidth;
	}

	/** The opacity property sets the opacity of the Component. Opacity is the degree to which content behind a Component is hidden, and is the opposite of transparency. */
	public set opacity(value: number) {
		assertIsFromZeroToOne(value);
		this.#opacity = value;
		this.#opacityChanged = true;
		this.invalidateProperties();
	}

	public get opacity(): number {
		return this.#opacity;
	}

	public set parentAutoLayout(value: 'grid' | 'horizontal' | 'vertical' | 'wrap') {
		if (this.#parentAutoLayout === value) {
			return;
		}
		assertIsValidAutoLayout(value);
		this.#parentAutoLayout = value;
		this.#parentAutoLayoutChanged = true;
		this.invalidateProperties();
	}

	public get parentAutoLayout(): 'grid' | 'horizontal' | 'vertical' | 'wrap' {
		return this.#parentAutoLayout;
	}

	/** The position property defines how the component should be positioned. Either by the parent container or the component itself with the related top, right, bottom and left properties. */
	public set position(value: 'absolute' | 'fixed' | 'relative') {
		if (this.#position === value) {
			return;
		}
		this.#position = value;
		this.#positionChanged = true;
		this.invalidateProperties();
	}

	public get position(): 'absolute' | 'fixed' | 'relative' {
		return this.#position;
	}

	/** The right property defines how many pixels the component will be positioned relative to the right edge of the parent container. */
	public set right(value: number) {
		if (this.#right === value) {
			return;
		}
		this.#right = value;
		this.#rightChanged = true;
		this.invalidateProperties();
	}

	public get right(): number {
		return this.#right;
	}

	/** The top property defines how many pixels the component will be positioned relative to the top edge of the parent container. */
	public set top(value: number) {
		if (this.#top === value) {
			return;
		}
		this.#top = value;
		this.#topChanged = true;
		this.invalidateProperties();
	}

	public get top(): number {
		return this.#top;
	}

	/** Visibility acts like visible, but still occupies the rendering size. */
	public set visibility(value: boolean) {
		assertIsBoolean(value);
		this.#visibility = value;
		this.#visibilityChanged = true;
		this.invalidateProperties();
	}

	public get visibility(): boolean {
		return this.#visibility;
	}

	/** Visible completely hides the component when set to false, otherwise shown. */
	public set visible(value: boolean) {
		assertIsValidVisible(value);
		this.#visible = value;
		this.#visibleChanged = true;
		this.invalidateProperties();
	}

	public get visible(): boolean {
		return this.#visible;
	}

	/**
	 * Width should be a number, "fill" or "hug".
	 *
	 * If a number, it represents the width in pixels, and must be a non negative number.
	 *
	 * If "fill", the component should expand it's width as much as possible
	 *
	 * If "hug", the component should shrink it's width as much as possible.
	 */
	public set width(value: 'fill' | 'hug' | number) {
		assertIsValidSize(value);
		this.#width = value;
		this.#widthChanged = true;
		this.invalidateProperties();
	}

	public get width(): 'fill' | 'hug' | number {
		return this.#width;
	}

	/**
	 * The z-index CSS property sets the z-order of a positioned element and its descendants or flex and grid items.
	 *
	 * Overlapping elements with a larger z-index cover those with a smaller one.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/z-index} for syntax
	 */
	public set zIndex(value: 'auto' | number) {
		if (this.#zIndex === value) {
			return;
		}
		assertIsValidZIndex(value);
		this.#zIndex = value;
		this.#zIndexChanged = true;
		this.invalidateProperties();
	}

	public get zIndex(): 'auto' | number {
		return this.#zIndex;
	}

	#bottom: number;

	#bottomChanged: boolean;

	#connected: boolean;

	#cursor: 'default' | 'pointer';

	#cursorChanged: boolean;

	#effects: DropShadow[] | null;

	#effectsChanged: boolean;

	#hasInitialized: boolean;

	#height: 'fill' | 'hug' | number;

	#heightChanged: boolean;

	#left: number;

	#leftChanged: boolean;

	#maxHeight: number;

	#maxHeightChanged: boolean;

	#maxWidth: number;

	#maxWidthChanged: boolean;

	#minHeight: number;

	#minHeightChanged: boolean;

	#minWidth: number;

	#minWidthChanged: boolean;

	#opacity: number;

	#opacityChanged: boolean;

	#parentAutoLayout: 'grid' | 'horizontal' | 'vertical' | 'wrap';

	#parentAutoLayoutChanged: boolean;

	#position: 'absolute' | 'fixed' | 'relative';

	#positionChanged: boolean;

	#right: number;

	#rightChanged: boolean;

	#top: number;

	#topChanged: boolean;

	#visibility: boolean;

	#visibilityChanged: boolean;

	#visible: boolean;

	#visibleChanged: boolean;

	#width: 'fill' | 'hug' | number;

	#widthChanged: boolean;

	#zIndex: 'auto' | number;

	#zIndexChanged: boolean;

	public constructor() {
		super();
		this.#bottom = Number.NaN;
		this.#bottomChanged = false;
		this.#connected = false;
		this.#cursor = 'default';
		this.#cursorChanged = false;
		this.#effects = null;
		this.#effectsChanged = false;
		this.#hasInitialized = false;
		this.#height = 'hug';
		this.#heightChanged = false;
		this.#left = Number.NaN;
		this.#leftChanged = false;
		this.#maxHeight = Infinity;
		this.#maxHeightChanged = false;
		this.#maxWidth = Infinity;
		this.#maxWidthChanged = false;
		this.#minHeight = 0;
		this.#minHeightChanged = false;
		this.#minWidth = 0;
		this.#minWidthChanged = false;
		this.#opacity = 1;
		this.#opacityChanged = false;
		this.#parentAutoLayout = 'horizontal';
		this.#parentAutoLayoutChanged = false;
		this.#position = 'relative';
		this.#positionChanged = false;
		this.#right = Number.NaN;
		this.#rightChanged = false;
		this.#top = Number.NaN;
		this.#topChanged = false;
		this.#visibility = true;
		this.#visibilityChanged = false;
		this.#visible = true;
		this.#visibleChanged = false;
		this.#width = 'hug';
		this.#widthChanged = false;
		this.#zIndex = 'auto';
		this.#zIndexChanged = false;
	}

	protected attributeChangedCallback(name: string, _: string, value: string) {
		if (name === 'cursor') {
			this.#cursorAttributeChanged(value);
		} else if (name === 'height') {
			this.#heightAttributeChanged(value);
		} else if (name === 'max-height') {
			this.#maxHeightAttributeChanged(value);
		} else if (name === 'max-width') {
			this.#maxWidthAttributeChanged(value);
		} else if (name === 'min-height') {
			this.#minHeightAttributeChanged(value);
		} else if (name === 'min-width') {
			this.#minWidthAttributeChanged(value);
		} else if (name === 'opacity') {
			this.#opacityAttributeChanged(value);
		} else if (name === 'width') {
			this.#widthAttributeChanged(value);
		}
	}

	protected commitProperties(): void {
		if (this.#cursorChanged) {
			this.#commitCursor();
		}
		if (this.#effectsChanged) {
			this.#commitEffects();
		}
		if (this.#opacityChanged) {
			this.#commitOpacity();
		}
		if (this.#parentAutoLayoutChanged) {
			this.#commitParentAutoLayout();
		}
		if (this.#visibilityChanged) {
			this.#commitVisibility();
		}
		if (this.#visibleChanged) {
			this.#commitVisible();
		}
		this.#commitSizingProperties();
		if (this.#positionChanged) {
			this.#commitPosition();
		}
		if (this.#topChanged) {
			this.#commitTop();
		}
		if (this.#rightChanged) {
			this.#commitRight();
		}
		if (this.#bottomChanged) {
			this.#commitBottom();
		}
		if (this.#leftChanged) {
			this.#commitLeft();
		}
	}

	/**
	 * Disconnected() is called every time the Component is removed from the DOM.
	 *
	 * Use it to clean up anything that lives outside the Component and would otherwise leak, like window event listeners or Observable subscriber functions.
	 *
	 * Unlike initialize(), this can be called more than once, since a Component can be removed and added again.
	 *
	 * Don't forget to call super.disconnected(), so super class components clean up correctly.
	 */
	protected disconnected(): void {
		// override in sub classes
	}

	/**
	 * Initialize() is called only once after the Component is added as a child.
	 *
	 * This is the place were we can safely call the web api's like style.disaplay = "flex" or appendChild()
	 *
	 * Use initialize() for initial state, creating children or add event listeners.
	 *
	 * Don't forget to call super.initialize(), so super class components are initialized correctly.
	 */
	protected initialize(): void {
		// override in sub classes
		this.#setDefaultStyles();
	}

	/** InvalidateProperties() is invoked by property setters, to trigger an invalidation pass that will commit the changes. */
	protected invalidateProperties(): void {
		if (this.#connected) {
			this.commitProperties();
		}
	}

	#commitBottom(): void {
		this.#bottomChanged = false;
		if (Number.isNaN(this.bottom)) {
			this.style.bottom = '';
		} else {
			this.style.bottom = `${this.bottom}px`;
		}
	}

	#commitCursor(): void {
		this.#cursorChanged = false;
		this.style.cursor = this.cursor;
	}

	#commitEffects(): void {
		this.#effectsChanged = false;
		if (this.effects === null) {
			this.style.boxShadow = '';
			return;
		}
		// create an Array of the effects (DropShadow) as style strings.
		const effectStringArray = this.effects.map((effect) => effect.toStyleString());
		// combine these style strings with a comma and space between them
		this.style.boxShadow = effectStringArray.join(', ');
	}

	#commitHeight(): void {
		this.#heightChanged = false;
		this.#updateHeightStyles();
	}

	#commitLeft(): void {
		this.#leftChanged = false;
		if (Number.isNaN(this.left)) {
			this.style.left = '';
		} else {
			this.style.left = `${this.left}px`;
		}
	}

	#commitMaxHeight(): void {
		this.#maxHeightChanged = false;
		this.style.maxHeight = `${this.maxHeight}px`;
	}

	#commitMaxWidth(): void {
		this.#maxWidthChanged = false;
		this.style.maxWidth = `${this.maxWidth}px`;
	}

	#commitMinHeight(): void {
		this.#minHeightChanged = false;
		this.style.minHeight = `${this.minHeight}px`;
	}

	#commitMinWidth(): void {
		this.#minWidthChanged = false;
		this.style.minWidth = `${this.minWidth}px`;
	}

	#commitOpacity(): void {
		this.#opacityChanged = false;
		this.style.opacity = `${this.opacity}`;
	}

	#commitParentAutoLayout(): void {
		this.#parentAutoLayoutChanged = false;
		this.#commitWidth();
		this.#commitHeight();
	}

	#commitPosition(): void {
		this.#positionChanged = false;
		this.style.position = this.position;
	}

	#commitRight(): void {
		this.#rightChanged = false;
		if (Number.isNaN(this.right)) {
			this.style.right = '';
		} else {
			this.style.right = `${this.#right}px`;
		}
	}

	#commitSizingProperties(): void {
		if (this.#heightChanged) {
			this.#commitHeight();
		}
		if (this.#maxHeightChanged) {
			this.#commitMaxHeight();
		}
		if (this.#maxWidthChanged) {
			this.#commitMaxWidth();
		}
		if (this.#minHeightChanged) {
			this.#commitMinHeight();
		}
		if (this.#minWidthChanged) {
			this.#commitMinWidth();
		}
		if (this.#widthChanged) {
			this.#commitWidth();
		}
		if (this.#zIndexChanged) {
			this.#commitZIndex();
		}
	}

	#commitTop(): void {
		this.#topChanged = false;
		if (Number.isNaN(this.top)) {
			this.style.top = '';
		} else {
			this.style.top = `${this.top}px`;
		}
	}

	#commitVisibility(): void {
		this.#visibilityChanged = false;
		if (this.visibility) {
			this.style.visibility = 'visible';
		} else {
			this.style.visibility = 'hidden';
		}
	}

	#commitVisible(): void {
		this.#visibleChanged = false;
		if (this.visible === true) {
			this.style.display = 'inline-flex';
		} else {
			this.style.display = 'none';
		}
	}

	#commitWidth(): void {
		this.#widthChanged = false;
		this.#updateWidthStyles();
	}

	#commitZIndex(): void {
		this.#zIndexChanged = false;
		if (this.zIndex === 'auto') {
			this.style.zIndex = '';
		} else {
			this.style.zIndex = this.zIndex.toString();
		}
	}

	#cursorAttributeChanged(value: string): void {
		assertIsValidCursor(value);
		this.cursor = value;
	}

	#heightAttributeChanged(value: string): void {
		if (value === 'fill' || value === 'hug') {
			this.height = value;
		} else {
			this.height = Number(value);
		}
	}

	#maxHeightAttributeChanged(value: string): void {
		this.maxHeight = Number(value);
	}

	#maxWidthAttributeChanged(value: string): void {
		this.maxWidth = Number(value);
	}

	#minHeightAttributeChanged(value: string): void {
		this.minHeight = Number(value);
	}

	#minWidthAttributeChanged(value: string): void {
		this.minWidth = Number(value);
	}

	#opacityAttributeChanged(value: string): void {
		this.opacity = Number(value);
	}

	#setDefaultStyles(): void {
		this.style.background = '';
		this.style.boxSizing = 'border-box';
		this.style.borderRadius = '';
		this.style.cursor = '';
		this.style.display = 'inline-block';
		this.style.filter = '';
		this.style.maxHeight = '';
		this.style.maxWidth = '';
		this.style.minHeight = '0px';
		this.style.minWidth = '0px';
		this.style.opacity = '';
		this.style.outline = 'none';
		this.style.position = 'relative';
	}

	/** We could have checked if this.parentElement was instanceof Container, but that will cause a circular dependency. So we use 'in' instead. */
	#setParentAutoLayout(): void {
		if (this.parentElement !== null && 'autoLayout' in this.parentElement) {
			const autoLayout = this.parentElement.autoLayout as 'horizontal' | 'vertical' | 'wrap';
			this.parentAutoLayout = autoLayout;
		}
	}

	/**
	 * Lays the component out along its parent's main axis, or across it.
	 *
	 * Which of those height is depends on the parent: in a vertical container the main axis is the height, and in a horizontal one it is the width. That is why flex-basis is set here only for a vertical parent — it is a main-axis property, and setting it across the axis would say nothing.
	 * {@link Component.updateWidthStyles} owns it the other way round, and between them each of the two axes is touched by exactly one of the two.
	 */
	#updateHeightStyles(): void {
		if (this.height === 'fill') {
			this.#updateHeightStylesFill();
		} else if (this.height === 'hug') {
			this.#updateHeightStylesHug();
		} else {
			this.#updateHeightStylesPixel();
		}
	}

	#updateHeightStylesFill(): void {
		this.style.flexShrink = '';
		if (this.parentAutoLayout === 'horizontal' || this.parentAutoLayout === 'wrap') {
			this.style.height = '';
			this.style.alignSelf = 'stretch';
			if (this.#width !== 'fill') {
				this.style.flexGrow = '';
			}
		} else if (this.parentAutoLayout === 'vertical') {
			this.style.height = '';
			this.style.flexGrow = '1';
			// Without this the basis is the content, and two filling children share only what is
			// left over rather than the whole: each ends up its own content height plus half the
			// remainder, so they differ by exactly as much as their contents do. Nought is what
			// makes "fill" mean an equal share, which is what width has always done.
			this.style.flexBasis = '0%';
			if (this.#width !== 'fill') {
				this.style.alignSelf = '';
			}
		}
	}

	#updateHeightStylesHug(): void {
		if (this.parentAutoLayout === 'horizontal' || this.parentAutoLayout === 'wrap') {
			this.style.height = '';
			this.style.alignSelf = '';
		} else if (this.parentAutoLayout === 'vertical') {
			this.style.height = '';
			this.style.flexGrow = '';
			this.style.flexShrink = '0';
			this.style.flexBasis = '';
		}
	}

	#updateHeightStylesPixel(): void {
		this.style.height = `${this.height}px`;
		if (this.parentAutoLayout === 'vertical') {
			this.style.flexGrow = '';
			this.style.flexShrink = '0';
			this.style.flexBasis = '';
		}
	}

	#updateWidthStyles(): void {
		if (this.width === 'fill') {
			this.#updateWidthStylesFill();
		} else if (this.width === 'hug') {
			this.#updateWidthStylesHug();
		} else {
			this.#updateWidthStylesPixel();
		}
	}

	#updateWidthStylesFill(): void {
		if (this.parentAutoLayout === 'horizontal' || this.parentAutoLayout === 'wrap') {
			this.style.width = '';
			this.style.flexGrow = '1';
			this.style.flexBasis = '0%';
			if (this.#height !== 'fill') {
				this.style.alignSelf = '';
			}
		} else if (this.parentAutoLayout === 'vertical') {
			this.style.width = '';
			this.style.alignSelf = 'stretch';
			if (this.#height !== 'fill') {
				this.style.flexGrow = '';
			}
		}
	}

	#updateWidthStylesHug(): void {
		if (this.parentAutoLayout === 'horizontal' || this.parentAutoLayout === 'wrap') {
			this.style.width = '';
			this.style.flexGrow = '';
			this.style.flexBasis = '';
		} else if (this.parentAutoLayout === 'vertical') {
			this.style.width = '';
			this.style.alignSelf = '';
		}
	}

	#updateWidthStylesPixel(): void {
		this.style.width = `${this.width}px`;
		if (this.parentAutoLayout === 'horizontal' || this.parentAutoLayout === 'wrap') {
			this.style.flexGrow = '';
			this.style.flexShrink = '0';
			this.style.flexBasis = '';
		}
	}

	#widthAttributeChanged(value: string): void {
		if (value === 'fill' || value === 'hug') {
			this.width = value;
		} else {
			this.width = Number(value);
		}
	}

	/**
	 * ConnectedCallback() is invoked immediately by the browser, when the component is added as a child of parent component.
	 *
	 * ConnectedCallback() can be called multiple times, so we use the connected flag to prevent invalidation being invoked too many times.
	 */
	private connectedCallback(): void {
		if (this.#connected === false) {
			this.#connected = true;
			if (this.#hasInitialized === false) {
				this.#hasInitialized = true;
				this.initialize();
			}
			this.#setParentAutoLayout();
			this.commitProperties();
		}
	}

	/** DisconnectedCallback() is invoked by the browser, when the component is removed from the parent component. */
	private disconnectedCallback(): void {
		this.#connected = false;
		this.disconnected();
	}

	/**
	 * Invoked by the browser in place of disconnectedCallback() and connectedCallback() when the component is moved with moveBefore(), which keeps its state — see Container.moveComponentBefore.
	 *
	 * Nothing happens here, and that is the point: a move is not a removal followed by an insertion. The component stays connected, so nothing is torn down — a subclass that unsubscribes from a model in disconnected() would otherwise lose its subscription for good, since it subscribed in its constructor — and nothing
	 * is committed again, since every property is already on the element. It has to be defined all the same: without a connectedMoveCallback() the browser fires the other two.
	 */
	private connectedMoveCallback(): void {
		// still connected, and still committed: there is nothing to take down or put back
	}
}
customElements.define('ac-component', Component);

function assertIsFromZeroToOne(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0 && value <= 1) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be from 0 to 1`);
}

function assertIsNonNegative(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number`);
}

function assertIsValidAutoLayout(value: unknown): asserts value is 'grid' | 'horizontal' | 'vertical' | 'wrap' {
	if (value === 'grid' || value === 'horizontal' || value === 'vertical' || value === 'wrap') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "grid", "horizontal", "vertical" or "wrap"`);
}

function assertIsValidCursor(value: unknown): asserts value is 'default' | 'pointer' {
	if (value === 'default' || value === 'pointer') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "" or "pointer"`);
}

function assertIsValidEffects(value: unknown): asserts value is DropShadow[] | null {
	if (value === null || (Array.isArray(value) && value.every((entry) => entry instanceof DropShadow))) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be Array with instances of DropShadow`);
}

function assertIsValidSize(value: unknown): asserts value is 'fill' | 'hug' | number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	if (value === 'fill' || value === 'hug') {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number, "fill" or "hug"`);
}

function assertIsValidVisible(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be boolean`);
}

function assertIsValidZIndex(value: unknown): asserts value is 'auto' | number {
	if (value === 'auto' || Number.isInteger(value)) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "auto" or an Integer`);
}
